import { useState, useEffect, useCallback, useRef } from "react";
import * as Sentry from "../lib/errorReporting";
import { authedApiFetch } from "../lib/api";

/**
 * Call /api/beta-status after sign-in, expose access state to the component tree.
 * Resets on sign-out. Memory-only — no localStorage or Capacitor storage.
 *
 * The result belongs to the user it was fetched for (#1503 pre-Codex FC-4): after an account switch nothing of the
 * previous user's result is returned, on any render, until the new user's answer arrives. Overlapping checks commit
 * only the newest. `auto: false` checks only when refetch() is called (the SlipSync sheet re-checks on each open,
 * FC-2/FC-3), so it never fires a second check at sign-in beside App's instance.
 *
 * @param {{ user: { id: string } | null, auto?: boolean }} options
 * @returns {{ allowed: boolean|null, tier: string|null, status: string|null, loading: boolean, error: string|null, checkedAt: number|null, refetch: () => void, clear: () => void }}
 */
const EMPTY = Object.freeze({ userId: null, allowed: null, tier: null, status: null, error: null, checkedAt: null });

// A failed check (401 / 5xx / network) never lifts a known denial for the same user (#1503 delta FC-1, escape-log
// #1458 class): only a fresh allowed:true does. An earlier allow is not kept: unknown, the server decides.
function failedCheck(prev, forUser, error) {
  const keepDenial = prev.userId === forUser && prev.allowed === false;
  return keepDenial
    ? { ...EMPTY, userId: forUser, allowed: false, status: prev.status, tier: null, error }
    : { ...EMPTY, userId: forUser, error };
}

export function useBetaStatus({ user, auto = true }) {
  const userId = user?.id || null;
  // One state object tagged with the user it belongs to; read only when it matches the current user.
  const [result, setResult] = useState(EMPTY);
  const [loadingFor, setLoadingFor] = useState(null);

  const prevUserIdRef = useRef(null);
  const fetchedRef = useRef(false);
  const seqRef = useRef(0);
  // auto:false: armed only by a refetch() for the current user; an account switch disarms it (#1503 delta, LOW).
  const armedRef = useRef(false);
  const [fetchTrigger, setFetchTrigger] = useState(0);

  const clear = useCallback(() => {
    seqRef.current += 1;
    setResult(EMPTY);
    setLoadingFor(null);
  }, []);

  const refetch = useCallback(() => {
    fetchedRef.current = false;
    armedRef.current = true;
    setFetchTrigger((n) => n + 1);
  }, []);

  useEffect(() => {
    // Sign-out: reset everything
    if (!userId) {
      prevUserIdRef.current = null;
      fetchedRef.current = false;
      armedRef.current = false;
      clear();
      return;
    }

    // New user sign-in: reset fetched flag
    if (userId !== prevUserIdRef.current) {
      const switched = prevUserIdRef.current !== null;
      prevUserIdRef.current = userId;
      fetchedRef.current = false;
      if (switched) armedRef.current = false;
    }

    // Already fetched for this session (unless refetch triggered); auto:false waits for refetch().
    if (fetchedRef.current) return;
    if (!auto && !armedRef.current) return;

    let cancelled = false;
    fetchedRef.current = true;
    const seq = ++seqRef.current;
    const forUser = userId;
    const stale = () => cancelled || seq !== seqRef.current;
    setLoadingFor(forUser);

    authedApiFetch("/api/beta-status", { method: "POST" })
      .then(async (res) => {
        if (stale()) return;

        if (res.status === 401) {
          setResult((prev) => failedCheck(prev, forUser, "Session expired"));
          setLoadingFor(null);
          return;
        }

        if (!res.ok) {
          // 503 or other server error — unavailable, not denied (a known denial is kept)
          setResult((prev) => failedCheck(prev, forUser, "Access check unavailable"));
          setLoadingFor(null);
          return;
        }

        const data = await res.json();
        if (stale()) return;
        // The server says whose answer this is (#1510 Codex r1 P1). authedApiFetch reads the live session on its
        // own, so during an account switch the request can authenticate another account. Commit only an answer for
        // the user this check was for; anything else (another id, or none) is treated as a failed check.
        if (!data || data.user_id !== forUser) {
          setResult((prev) => failedCheck(prev, forUser, "Access check did not match this account"));
          setLoadingFor(null);
          return;
        }
        setResult({
          userId: forUser,
          allowed: data.allowed,
          tier: data.tier || null,
          status: data.status || null,
          error: null,
          checkedAt: Date.now(),
        });
        setLoadingFor(null);
      })
      .catch((err) => {
        if (stale()) return;
        Sentry.captureException(err);
        setResult((prev) => failedCheck(prev, forUser, err?.message || "Network error"));
        setLoadingFor(null);
      });

    return () => {
      cancelled = true;
    };
  }, [userId, fetchTrigger, clear, auto]);

  const mine = userId != null && result.userId === userId;
  return {
    allowed: mine ? result.allowed : null,
    tier: mine ? result.tier : null,
    status: mine ? result.status : null,
    // Loading while a check for this user is in flight, or (auto) before this user's first answer has arrived.
    loading: userId != null && (loadingFor === userId || (auto && !mine)),
    error: mine ? result.error : null,
    checkedAt: mine ? result.checkedAt : null,
    refetch,
    clear,
  };
}
